import { Button, FlexContainer } from '@act/storybook';
import { Typography } from '@mui/material';
import { PlusIcon } from '@phosphor-icons/react';
import {
  Control,
  FormState,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormGetValues,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { FieldArrayWithId } from 'react-hook-form';
import {
  ActionFormResourceDetails,
  ActionFormValues,
} from 'react-components/dialog-forms/components/useNeedActionFormtypes';
import { NeedActionResourceSlot } from './NeedActionResourceSlot';

const emptyResourceRow = (): ActionFormResourceDetails => ({
  id: -1,
  resourceName: '',
  contactName: '',
  phoneNumber: undefined,
  url: '',
  streetAddress: '',
  city: '',
  state: '',
  zipcode: '',
  shouldDelete: false,
});

export interface NeedActionResourcesSectionProps {
  control: Control<ActionFormValues>;
  formState: FormState<ActionFormValues>;
  setValue: UseFormSetValue<ActionFormValues>;
  getValues: UseFormGetValues<ActionFormValues>;
  fields: FieldArrayWithId<ActionFormValues, 'resourceDetails', 'key'>[];
  append: UseFieldArrayAppend<ActionFormValues, 'resourceDetails'>;
  remove: UseFieldArrayRemove;
  /** Needs metadata issue key for resource card icons. */
  needIssueKey?: string;
  disabled?: boolean;
}

/**
 * Need-action resource referrals: Places search, recent CBO cards, and per-resource forms.
 *
 * @param props - Field array bindings from the parent action form.
 * @returns Resource referral section for add/edit action drawers.
 */
export function NeedActionResourcesSection({
  control,
  formState,
  setValue,
  getValues,
  fields,
  append,
  remove,
  needIssueKey,
  disabled = false,
}: NeedActionResourcesSectionProps) {
  const resourceDetails =
    useWatch({ control, name: 'resourceDetails' }) ?? getValues('resourceDetails') ?? [];

  return (
    <FlexContainer direction="column" gap={2} width="100%" overflowAllVisible>
      <Typography variant="subheadMedium" fontWeight="bold">
        Resources
      </Typography>
      {fields.map((field, index) => (
        <NeedActionResourceSlot
          key={field.key}
          control={control}
          formState={formState}
          setValue={setValue}
          getValues={getValues}
          resourceDetailsIndex={index}
          details={resourceDetails[index] ?? emptyResourceRow()}
          needIssueKey={needIssueKey}
          onRemove={() => remove(index)}
          disabled={disabled}
        />
      ))}
      <Button trackingName="append-button"
        variant="outlined"
        onClick={() => append(emptyResourceRow())}
        startIcon={<PlusIcon size={16} />}
        disabled={disabled}
      >
        Add resource
      </Button>
    </FlexContainer>
  );
}
